// src/utils/contentExportUtils.js
/**
 * Professional content export utilities for generated articles and essays
 * Provides formatted Word documents with headers, footers, and proper styling
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  PageBreak,
  Header,
  Footer,
  PageNumber,
  BorderStyle,
  convertInchesToTwip
} from 'docx';
import { wordExportConfig } from '../config/wordExportConfig';

const STYLES = {
  fonts: wordExportConfig.typography.fonts,
  colors: wordExportConfig.colors,
  spacing: wordExportConfig.spacing,
  sizes: wordExportConfig.typography.sizes
};

/**
 * Creates document header for generated content
 */
const createContentHeader = (title) => {
  return new Header({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: title.length > 60 ? title.substring(0, 57) + '...' : title,
            font: STYLES.fonts.heading,
            size: STYLES.sizes.footer,
            color: STYLES.colors.secondary
          })
        ],
        alignment: AlignmentType.CENTER,
        border: {
          bottom: {
            color: STYLES.colors.accent,
            space: 1,
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: {
          after: 120
        }
      })
    ]
  });
};

/**
 * Creates document footer with page numbers, branding, and date
 */
const createContentFooter = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return new Footer({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: `Generated by DraftEngine | ${currentDate}`,
            font: STYLES.fonts.body,
            size: STYLES.sizes.footer,
            color: STYLES.colors.secondary
          }),
          new TextRun({
            text: "\t\t",
          }),
          new TextRun({
            children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
            font: STYLES.fonts.body,
            size: STYLES.sizes.footer,
            color: STYLES.colors.secondary
          })
        ],
        alignment: AlignmentType.CENTER,
        border: {
          top: {
            color: STYLES.colors.accent,
            space: 1,
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: {
          before: 120
        }
      })
    ]
  });
};

/**
 * Creates title page for the generated content
 */
const createContentTitlePage = (title, metadata = {}) => {
  const {
    documentType = 'Article',
    approach = 'balanced',
    wordCount = null,
    author = null
  } = metadata;

  return [
    new Paragraph({
      children: [
        new TextRun({
          text: title,
          font: STYLES.fonts.heading,
          size: 44,
          color: STYLES.colors.primary,
          bold: true
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        before: convertInchesToTwip(3),
        after: convertInchesToTwip(1)
      }
    }),
    ...(author ? [
      new Paragraph({
        children: [
          new TextRun({
            text: author,
            font: STYLES.fonts.body,
            size: 28,
            color: STYLES.colors.secondary
          })
        ],
        alignment: AlignmentType.CENTER,
        spacing: {
          after: convertInchesToTwip(0.5)
        }
      })
    ] : []),
    new Paragraph({
      children: [
        new TextRun({
          text: documentType,
          font: STYLES.fonts.body,
          size: 24,
          color: STYLES.colors.secondary,
          italics: true
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: convertInchesToTwip(0.3)
      }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Approach: ${approach.charAt(0).toUpperCase() + approach.slice(1)}`,
          font: STYLES.fonts.body,
          size: 22,
          color: STYLES.colors.secondary
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: convertInchesToTwip(0.2)
      }
    }),
    ...(wordCount ? [
      new Paragraph({
        children: [
          new TextRun({
            text: `${wordCount.toLocaleString()} words`,
            font: STYLES.fonts.body,
            size: 22,
            color: STYLES.colors.secondary
          })
        ],
        alignment: AlignmentType.CENTER,
        spacing: {
          after: convertInchesToTwip(0.5)
        }
      })
    ] : []),
    new Paragraph({
      children: [
        new TextRun({
          text: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          font: STYLES.fonts.body,
          size: 22,
          color: STYLES.colors.secondary
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: convertInchesToTwip(2)
      }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "AI-Generated Content",
          font: STYLES.fonts.body,
          size: 18,
          color: STYLES.colors.secondary,
          italics: true
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: convertInchesToTwip(0.1)
      }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Created with DraftEngine",
          font: STYLES.fonts.body,
          size: 18,
          color: STYLES.colors.secondary,
          italics: true
        })
      ],
      alignment: AlignmentType.CENTER
    }),
    new Paragraph({
      children: [new PageBreak()]
    })
  ];
};

/**
 * Creates a Document Summary page with meta description and social excerpt
 */
const createDocumentSummaryPage = (metaDescription, socialExcerpt) => {
  const paragraphs = [];

  // Document Summary heading
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Document Summary',
          font: STYLES.fonts.heading,
          size: 32,
          color: STYLES.colors.primary,
          bold: true
        })
      ],
      heading: HeadingLevel.HEADING_1,
      spacing: {
        before: convertInchesToTwip(0.5),
        after: STYLES.spacing.afterHeading
      },
      border: {
        bottom: {
          color: STYLES.colors.accent,
          space: 1,
          size: 8,
          style: BorderStyle.SINGLE
        }
      }
    })
  );

  // Meta description text
  if (metaDescription) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: metaDescription,
            font: STYLES.fonts.body,
            size: 24,
            italics: true
          })
        ],
        alignment: AlignmentType.JUSTIFIED,
        spacing: {
          after: convertInchesToTwip(0.5),
          line: STYLES.spacing.line
        }
      })
    );
  }

  // Social Media Excerpt heading
  if (socialExcerpt) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'Social Media Excerpt',
            font: STYLES.fonts.heading,
            size: 28,
            color: STYLES.colors.primary,
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: {
          before: STYLES.spacing.beforeHeading,
          after: STYLES.spacing.afterHeading
        }
      })
    );

    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: socialExcerpt,
            font: STYLES.fonts.body,
            size: 22,
            italics: true,
            color: STYLES.colors.secondary
          })
        ],
        alignment: AlignmentType.LEFT,
        spacing: {
          after: convertInchesToTwip(0.3),
          line: STYLES.spacing.line
        },
        indent: {
          left: convertInchesToTwip(0.25)
        }
      })
    );
  }

  // Page break after summary
  paragraphs.push(
    new Paragraph({
      children: [new PageBreak()]
    })
  );

  return paragraphs;
};

/**
 * Creates a References section from a references list
 */
const createReferencesSection = (referencesList) => {
  const paragraphs = [];

  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'References',
          font: STYLES.fonts.heading,
          size: 32,
          color: STYLES.colors.primary,
          bold: true
        })
      ],
      heading: HeadingLevel.HEADING_1,
      spacing: {
        before: convertInchesToTwip(0.5),
        after: STYLES.spacing.afterHeading
      },
      border: {
        bottom: {
          color: STYLES.colors.accent,
          space: 1,
          size: 8,
          style: BorderStyle.SINGLE
        }
      }
    })
  );

  // Handle references as array of strings or a single string block
  const refs = Array.isArray(referencesList) ? referencesList : referencesList.split('\n').filter(r => r.trim());

  refs.forEach((ref) => {
    const refText = typeof ref === 'string' ? ref.trim() : (ref.formatted || ref.text || String(ref));
    if (!refText) return;

    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: refText,
            font: STYLES.fonts.body,
            size: 22
          })
        ],
        alignment: AlignmentType.LEFT,
        spacing: {
          after: 180,
          line: STYLES.spacing.line
        },
        indent: {
          left: convertInchesToTwip(0.5),
          hanging: convertInchesToTwip(0.5)
        }
      })
    );
  });

  return paragraphs;
};

/**
 * Parse markdown-style content into structured paragraphs with formatting
 */
const parseContentToParagraphs = (content, sectionImages = {}) => {
  const paragraphs = [];
  const lines = content.split('\n');
  let sectionIndex = 0;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    // Skip empty lines
    if (!line) {
      i++;
      continue;
    }

    // Handle H2 headings (## Heading)
    if (line.startsWith('## ')) {
      const headingText = line.replace(/^##\s+/, '');
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: headingText,
              font: STYLES.fonts.heading,
              size: 32,
              color: STYLES.colors.primary,
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_1,
          spacing: {
            before: STYLES.spacing.beforeHeading,
            after: STYLES.spacing.afterHeading
          },
          border: {
            bottom: {
              color: STYLES.colors.accent,
              space: 1,
              size: 8,
              style: BorderStyle.SINGLE
            }
          }
        })
      );

      // Check for a section image placeholder
      const sectionKey = String(sectionIndex);
      const imageEntry = sectionImages[sectionKey] || sectionImages[sectionIndex];
      if (imageEntry) {
        const prompt = typeof imageEntry === 'string' ? imageEntry : (imageEntry.imagePrompt || imageEntry.prompt || '');
        if (prompt) {
          paragraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `[Illustration: ${prompt}]`,
                  font: STYLES.fonts.body,
                  size: 20,
                  color: STYLES.colors.muted,
                  italics: true
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 120,
                after: 240
              }
            })
          );
        }
      }
      sectionIndex++;

      i++;
      continue;
    }

    // Handle H3 headings (### Heading)
    if (line.startsWith('### ')) {
      const headingText = line.replace(/^###\s+/, '');
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: headingText,
              font: STYLES.fonts.heading,
              size: 28,
              color: STYLES.colors.primary,
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: {
            before: STYLES.spacing.beforeHeading - 120,
            after: STYLES.spacing.afterHeading - 60
          }
        })
      );
      i++;
      continue;
    }

    // Handle bullet points
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const bulletText = line.replace(/^[-*]\s+/, '');
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `• ${bulletText}`,
              font: STYLES.fonts.body,
              size: 24
            })
          ],
          alignment: AlignmentType.JUSTIFIED,
          spacing: {
            after: 120,
            line: STYLES.spacing.line
          },
          indent: {
            left: convertInchesToTwip(0.25),
            hanging: convertInchesToTwip(0.15)
          }
        })
      );
      i++;
      continue;
    }

    // Regular paragraph with inline formatting support
    paragraphs.push(createFormattedParagraph(line));
    i++;
  }

  return paragraphs;
};

/**
 * Creates a paragraph with inline bold and italic formatting
 */
const createFormattedParagraph = (text) => {
  const children = [];
  let currentText = text;

  // Simple regex for bold (**text**) and italic (*text*)
  const boldRegex = /\*\*(.*?)\*\*/g;
  const italicRegex = /\*(.*?)\*/g;

  // Split by bold first
  const boldParts = currentText.split(boldRegex);

  for (let i = 0; i < boldParts.length; i++) {
    if (i % 2 === 0) {
      // Normal or italic text
      const italicParts = boldParts[i].split(italicRegex);
      for (let j = 0; j < italicParts.length; j++) {
        if (italicParts[j]) {
          children.push(
            new TextRun({
              text: italicParts[j],
              font: STYLES.fonts.body,
              size: 24,
              italics: j % 2 === 1
            })
          );
        }
      }
    } else {
      // Bold text
      children.push(
        new TextRun({
          text: boldParts[i],
          font: STYLES.fonts.body,
          size: 24,
          bold: true
        })
      );
    }
  }

  return new Paragraph({
    children: children.length > 0 ? children : [
      new TextRun({
        text: text,
        font: STYLES.fonts.body,
        size: 24
      })
    ],
    alignment: AlignmentType.JUSTIFIED,
    spacing: {
      after: STYLES.spacing.paragraph,
      line: STYLES.spacing.line
    }
  });
};

/**
 * Main export function for generated content
 */
export const exportGeneratedContent = async (job, options = {}) => {
  const {
    includeTitlePage = true,
    includeHeaders = true,
    includeFooters = true
  } = options;

  try {
    // Use refined title if available, fall back to outline title or default
    const title = job.refinedTitle || job.refined_title || job.outline?.title || 'Generated Content';
    const content = job.content || '';

    // Extract enhanced output fields
    const metaDescription = job.metaDescription || job.meta_description || null;
    const socialExcerpt = job.socialExcerpt || job.social_excerpt || null;
    const referencesList = job.referencesList || job.references_list || job.references || null;
    const sectionImages = job.sectionImages || job.section_images || {};

    // Calculate word count
    const wordCount = content.split(/\s+/).filter(w => w.length > 0).length;

    // Metadata from job settings
    const metadata = {
      documentType: job.settings?.document_type ?
        job.settings.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) :
        'Article',
      approach: job.settings?.approach || job.settings?.citation_style || 'balanced',
      wordCount: wordCount,
      author: null // Could add user name here if available
    };

    const children = [];

    // Add title page
    if (includeTitlePage) {
      children.push(...createContentTitlePage(title, metadata));
    }

    // Add Document Summary page with meta description and social excerpt
    if (metaDescription || socialExcerpt) {
      children.push(...createDocumentSummaryPage(metaDescription, socialExcerpt));
    }

    // Parse and add content with section image placeholders
    children.push(...parseContentToParagraphs(content, sectionImages));

    // Add references section at the end
    if (referencesList && (Array.isArray(referencesList) ? referencesList.length > 0 : referencesList.trim())) {
      children.push(
        new Paragraph({
          children: [new PageBreak()]
        })
      );
      children.push(...createReferencesSection(referencesList));
    }

    // Create document
    const doc = new Document({
      sections: [{
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            }
          }
        },
        headers: includeHeaders ? {
          default: createContentHeader(title)
        } : undefined,
        footers: includeFooters ? {
          default: createContentFooter()
        } : undefined,
        children: children
      }],
      styles: {
        default: {
          heading1: {
            run: {
              font: STYLES.fonts.heading,
              size: 32,
              bold: true,
              color: STYLES.colors.primary
            },
            paragraph: {
              spacing: {
                before: STYLES.spacing.beforeHeading,
                after: STYLES.spacing.afterHeading
              }
            }
          },
          heading2: {
            run: {
              font: STYLES.fonts.heading,
              size: 28,
              bold: true,
              color: STYLES.colors.primary
            },
            paragraph: {
              spacing: {
                before: STYLES.spacing.beforeHeading - 120,
                after: STYLES.spacing.afterHeading - 60
              }
            }
          },
          document: {
            run: {
              font: STYLES.fonts.body,
              size: 24
            },
            paragraph: {
              spacing: {
                line: STYLES.spacing.line,
                after: STYLES.spacing.paragraph
              }
            }
          }
        }
      }
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = title.replace(/[^a-zA-Z0-9\s-]/g, '').trim().replace(/\s+/g, '-').substring(0, 60);
    a.download = `${safeTitle}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Error exporting generated content:', error);
    throw error;
  }
};
